// Lightweight accessors over the native iOS (WKWebView) message-handler bridge.
//
// This is the iOS counterpart to androidBridge.ts, with one hard constraint:
// WKWebView's web -> native channel (window.webkit.messageHandlers.*.postMessage)
// is FIRE-AND-FORGET — it cannot return a value synchronously the way Android's
// @JavascriptInterface can. So there are no getters here (no getFcmToken, no
// getNotificationPermissionState). Anything native needs to report back — the
// FCM token, the permission state, incoming pushes — comes IN through
// window.NineFindsNative.* (see nativeBridge.ts), which native invokes via
// evaluateJavaScript.
//
// The only import is the light diagnostics logger (which does NOT import back
// here), so this stays free of import cycles like androidBridge.ts.
import { bridgeLog } from "./bridgeLog";

// All web -> native calls funnel through a single WKScriptMessageHandler named
// "nineFindsNative"; the specific request is carried in the `action` field.
type IosBridgeAction =
  | "onWebAppReady"
  | "requestNotificationPermission"
  | "requestMicrophonePermission"
  | "openAppSettings"
  // Open a URL in the EXTERNAL system browser (Safari), not this WKWebView.
  // Carries a `url` field. Native contract: open with
  // UIApplication.shared.open(url) so the user leaves the app for real Safari.
  | "openExternalUrl"
  // Downloads `url` and saves it to the user's photo library. Carries `url`
  // and `filename` fields. Native contract: fetch the URL and save via
  // PHPhotoLibrary (add-only access — see NSPhotoLibraryAddUsageDescription
  // in Info.plist). Mirrors the Android app's WebAppInterface.downloadImage.
  | "downloadImage";

function post(action: IosBridgeAction, extra?: Record<string, unknown>): void {
  bridgeLog(`-> native: ${action}`);
  try {
    window.webkit?.messageHandlers?.nineFindsNative?.postMessage({
      action,
      ...extra,
    });
  } catch (error) {
    console.warn(`iOS bridge "${action}" failed:`, error);
  }
}

/** True once the iOS app has registered the "nineFindsNative" handler. */
export function iosBridgeAvailable(): boolean {
  return (
    typeof window.webkit?.messageHandlers?.nineFindsNative !== "undefined"
  );
}

export function callOnWebAppReadyIos(): void {
  post("onWebAppReady");
}

/**
 * Asks native to run the notification-permission flow. Like Android, native is
 * expected to never dead-end: show the OS dialog when it can, else open the
 * app's Settings page. The result arrives asynchronously via
 * window.NineFindsNative.onPermissionChanged.
 */
export function requestIosNativeNotificationPermission(): void {
  post("requestNotificationPermission");
}

/**
 * Asks native to run the microphone-permission flow for the voice feature. Like
 * notifications, native never dead-ends: show the OS mic dialog when it can,
 * else open the app's Settings page. The result arrives asynchronously via
 * window.NineFindsNative.onPermissionChanged("microphone", state).
 */
export function requestIosMicrophonePermission(): void {
  post("requestMicrophonePermission");
}

/** Opens this app's system Settings page (camera/location recovery parity). */
export function openIosAppSettings(): void {
  post("openAppSettings");
}

/**
 * Asks native to open `url` in the EXTERNAL system browser (Safari), leaving
 * the app. Fire-and-forget like every other iOS bridge call. Used to steer
 * payment flows out of the WKWebView (App Store policy — no in-app purchase
 * links). Callers should fall back to window.open when the bridge isn't
 * available (see openExternalBrowser).
 */
export function openExternalUrlIos(url: string): void {
  if (!url) return;
  post("openExternalUrl", { url });
}

/**
 * Asks native to download `url` and save it into the user's photo library, as
 * `filename`. Fire-and-forget like every other iOS bridge call — there is no
 * completion signal back to the web (native logs failures to its own
 * console instead). Used by the gallery "download" fallback in
 * DownloadHelper.ts, since there's no in-WebView download manager to catch a
 * browser-style blob download.
 */
export function downloadImageIos(url: string, filename: string): void {
  if (!url) return;
  post("downloadImage", { url, filename });
}
